import { Router } from "express";
import Stripe from "stripe";
import { db } from "@workspace/db";
import {
  subscriptionsTable, plansTable, invoicesTable, companiesTable, notificationSettingsTable,
  usersTable,
} from "@workspace/db";
import { eq, and, desc } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { sendMail, subscriptionEventHtml } from "../lib/mailer";

/* ── Lazy Stripe client — reads DB settings first, then env var ── */
import { db as _db } from "@workspace/db";
import { sql as _sql } from "drizzle-orm";

let _stripe: Stripe | null = null;
let _stripeCacheKey: string | null = null;

async function getDbStripeKey(): Promise<string | null> {
  try {
    const rows = await _db.execute(_sql`SELECT value FROM platform_settings WHERE key = 'stripe_secret_key'`);
    return (rows.rows?.[0] as any)?.value ?? null;
  } catch { return null; }
}

async function getDbStripeWebhookSecret(): Promise<string | null> {
  try {
    const rows = await _db.execute(_sql`SELECT value FROM platform_settings WHERE key = 'stripe_webhook_secret'`);
    return (rows.rows?.[0] as any)?.value ?? null;
  } catch { return null; }
}

async function getStripe(): Promise<Stripe | null> {
  const key = (await getDbStripeKey()) ?? process.env.STRIPE_SECRET_KEY ?? null;
  if (!key) return null;
  if (key !== _stripeCacheKey) {
    _stripe = new Stripe(key);
    _stripeCacheKey = key;
  }
  return _stripe;
}

const FRONTEND_URL =
  process.env["FRONTEND_URL"] ??
  (process.env["REPLIT_DEV_DOMAIN"]
    ? `https://${process.env["REPLIT_DEV_DOMAIN"]}`
    : null);

const router = Router();

router.get("/subscriptions/current", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(404).json({ error: "No company profile" });

    // Prefer the active subscription; fall back to the most-recently-created one
    const rows = await db
      .select()
      .from(subscriptionsTable)
      .where(eq(subscriptionsTable.companyId, req.companyId))
      .orderBy(desc(subscriptionsTable.createdAt));

    const sub = [rows.find((r) => r.status === "active") ?? rows[0]];

    if (!sub[0]) return res.status(404).json({ error: "No active subscription" });

    const plan = sub[0].planId
      ? await db.select().from(plansTable).where(eq(plansTable.id, sub[0].planId)).limit(1)
      : [];

    res.json({ ...sub[0], plan: plan[0] ?? null });
  } catch (err) {
    next(err);
  }
});

router.post("/subscriptions/checkout", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const stripe = await getStripe();
    if (!stripe) return res.status(503).json({ error: "Payments not configured" });
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });

    const { planId } = req.body;
    const plan = await db
      .select()
      .from(plansTable)
      .where(eq(plansTable.id, planId))
      .limit(1);
    if (!plan[0]) return res.status(404).json({ error: "Plan not found" });

    const company = await db
      .select()
      .from(companiesTable)
      .where(eq(companiesTable.id, req.companyId))
      .limit(1);

    // Use pinned FRONTEND_URL — do not trust request Origin header for redirects
    const appUrl =
      FRONTEND_URL ??
      `${req.protocol}://${req.hostname}`;

    const session = await stripe.checkout.sessions.create({
      mode: "subscription",
      payment_method_types: ["card"],
      customer_email: req.dbUser!.email,
      metadata: {
        companyId: String(req.companyId),
        planId: String(planId),
        userId: String(req.dbUser!.id),
        companyName: company[0]?.name ?? "",
      },
      line_items: plan[0].stripePriceId
        ? [{ price: plan[0].stripePriceId, quantity: 1 }]
        : [
            {
              price_data: {
                currency: "sar",
                unit_amount: Math.round(Number(plan[0].priceSar) * 100),
                recurring: { interval: plan[0].billingPeriod === "yearly" ? "year" : "month" },
                product_data: { name: plan[0].name },
              },
              quantity: 1,
            },
          ],
      success_url: `${appUrl}/plans?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${appUrl}/plans`,
    });

    res.json({ url: session.url });
  } catch (err) {
    next(err);
  }
});

router.post("/subscriptions/cancel", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });

    const sub = await db
      .select()
      .from(subscriptionsTable)
      .where(eq(subscriptionsTable.companyId, req.companyId))
      .limit(1);

    if (!sub[0]) return res.status(404).json({ error: "No active subscription" });

    const stripe = await getStripe();
    if (stripe && sub[0].stripeSubscriptionId) {
      await stripe.subscriptions.update(sub[0].stripeSubscriptionId, {
        cancel_at_period_end: true,
      });
    }

    const updated = await db
      .update(subscriptionsTable)
      .set({ cancelAtPeriodEnd: true, updatedAt: new Date() })
      .where(eq(subscriptionsTable.id, sub[0].id))
      .returning();

    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

// Stripe Webhook — handles full subscription lifecycle
router.post("/stripe/webhook", async (req, res, next) => {
  try {
    const stripe = await getStripe();
    const webhookSecret = (await getDbStripeWebhookSecret()) ?? process.env.STRIPE_WEBHOOK_SECRET ?? null;

    // Log webhook receipt
    console.log("[WEBHOOK] Received webhook", {
      hasStripe: !!stripe,
      hasWebhookSecret: !!webhookSecret,
      signaturePresent: !!req.headers["stripe-signature"],
    });

    if (!stripe || !webhookSecret) {
      console.log("[WEBHOOK] No stripe or webhook secret, returning received=true");
      return res.json({ received: true });
    }

    const sig = req.headers["stripe-signature"] as string;
    let event: Stripe.Event;

    try {
      event = stripe.webhooks.constructEvent(req.body, sig, webhookSecret);
      console.log("[WEBHOOK] Signature verified, event type:", event.type);
    } catch (err) {
      console.error("[WEBHOOK] Invalid signature:", err);
      return res.status(400).json({ error: "Invalid signature" });
    }

    switch (event.type) {
      case "checkout.session.completed": {
        const session = event.data.object as Stripe.Checkout.Session;
        const { companyId, planId, companyName } = session.metadata ?? {};
        console.log("[WEBHOOK] checkout.session.completed", {
          companyId,
          planId,
          companyName,
          subscriptionId: session.subscription,
          customerId: session.customer,
        });
        if (!companyId || !planId) {
          console.log("[WEBHOOK] Missing companyId or planId, skipping");
          break;
        }

        const plan = await db
          .select()
          .from(plansTable)
          .where(eq(plansTable.id, Number(planId)))
          .limit(1);

        await db
          .insert(subscriptionsTable)
          .values({
            companyId: Number(companyId),
            planId: Number(planId),
            status: "active",
            stripeSubscriptionId: session.subscription as string,
            stripeCustomerId: session.customer as string,
          })
          .onConflictDoUpdate({
            target: subscriptionsTable.companyId,
            set: {
              planId: Number(planId),
              status: "active",
              stripeSubscriptionId: session.subscription as string,
              cancelAtPeriodEnd: false,
              updatedAt: new Date(),
            },
          });
        console.log("[WEBHOOK] Subscription created/updated for company:", companyId);

        const amount = Number(plan[0]?.priceSar ?? 0);
        const vat = amount * 0.15;
        await db.insert(invoicesTable).values({
          invoiceNumber: `INV-${Date.now()}`,
          companyId: Number(companyId),
          companyName: companyName ?? "Unknown",
          planName: plan[0]?.name ?? null,
          amountSar: String(amount),
          vatAmount: String(vat.toFixed(2)),
          totalAmount: String((amount + vat).toFixed(2)),
          status: "paid",
          stripeInvoiceId: session.invoice as string,
        }).onConflictDoNothing();

        await db
          .insert(notificationSettingsTable)
          .values({ companyId: Number(companyId) })
          .onConflictDoNothing();

        // Send subscription-created email to user
        if (session.metadata?.userId) {
          const user = await db
            .select({ email: usersTable.email, firstName: usersTable.firstName, language: usersTable.language })
            .from(usersTable)
            .where(eq(usersTable.id, Number(session.metadata.userId)))
            .limit(1);
          if (user[0]) {
            const isAr = user[0].language === "ar";
            sendMail({
              to: user[0].email,
              subject: isAr ? `تم تفعيل اشتراكك – ${plan[0]?.name ?? ""}` : `Subscription Activated – ${plan[0]?.name ?? ""}`,
              html: subscriptionEventHtml(user[0].firstName ?? "", "created", plan[0]?.name ?? "", isAr),
            }).catch(() => {});
          }
        }
        break;
      }

      case "customer.subscription.updated": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sub = event.data.object as any;
        const stripeStatus: string = sub.status ?? "";
        const status =
          stripeStatus === "active" ? "active" :
          stripeStatus === "past_due" ? "past_due" :
          stripeStatus === "canceled" ? "cancelled" :
          stripeStatus === "trialing" ? "trialing" : "past_due";

        await db
          .update(subscriptionsTable)
          .set({
            status: status as "active" | "cancelled" | "expired" | "trialing" | "past_due",
            cancelAtPeriodEnd: Boolean(sub.cancel_at_period_end),
            currentPeriodEnd: sub.current_period_end
              ? new Date(Number(sub.current_period_end) * 1000)
              : undefined,
            updatedAt: new Date(),
          })
          .where(eq(subscriptionsTable.stripeSubscriptionId, sub.id as string));
        break;
      }

      case "customer.subscription.deleted": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sub = event.data.object as any;
        const updated = await db
          .update(subscriptionsTable)
          .set({ status: "cancelled", cancelAtPeriodEnd: false, updatedAt: new Date() })
          .where(eq(subscriptionsTable.stripeSubscriptionId, sub.id as string))
          .returning();

        // Notify company user about cancellation — fire-and-forget
        if (updated[0]) {
          const companyUser = await db
            .select({ email: usersTable.email, firstName: usersTable.firstName, language: usersTable.language })
            .from(usersTable)
            .where(eq(usersTable.companyId, updated[0].companyId))
            .limit(1);
          if (companyUser[0]) {
            const isAr = companyUser[0].language === "ar";
            const planName = updated[0].planId
              ? (await db.select({ name: plansTable.name }).from(plansTable).where(eq(plansTable.id, updated[0].planId)).limit(1))[0]?.name ?? ""
              : "";
            sendMail({
              to: companyUser[0].email,
              subject: isAr ? `تم إلغاء اشتراكك – ${planName}` : `Subscription Cancelled – ${planName}`,
              html: subscriptionEventHtml(companyUser[0].firstName ?? "", "cancelled", planName, isAr),
            }).catch(() => {});
          }
        }
        break;
      }

      case "invoice.payment_succeeded": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inv = event.data.object as any;
        const subscriptionId = inv.subscription as string | null;
        if (!subscriptionId) break;

        const sub = await db
          .select()
          .from(subscriptionsTable)
          .where(eq(subscriptionsTable.stripeSubscriptionId, subscriptionId))
          .limit(1);
        if (!sub[0]) break;

        // Resolve company name and plan name for invoice quality
        const [renewalCompany, renewalPlan] = await Promise.all([
          db.select({ name: companiesTable.name })
            .from(companiesTable)
            .where(eq(companiesTable.id, sub[0].companyId))
            .limit(1),
          sub[0].planId
            ? db.select({ name: plansTable.name })
                .from(plansTable)
                .where(eq(plansTable.id, sub[0].planId))
                .limit(1)
            : Promise.resolve([] as Array<{ name: string }>),
        ]);

        // amount_paid already includes VAT — derive subtotal and VAT consistently
        const totalWithVat = (inv.amount_paid as number) / 100;
        const subtotal = totalWithVat / 1.15;
        const vatAmount = totalWithVat - subtotal;

        await db.insert(invoicesTable).values({
          invoiceNumber: `INV-${Date.now()}`,
          companyId: sub[0].companyId,
          companyName: renewalCompany[0]?.name ?? "Unknown",
          planName: renewalPlan[0]?.name ?? null,
          amountSar: String(subtotal.toFixed(2)),
          vatAmount: String(vatAmount.toFixed(2)),
          totalAmount: String(totalWithVat.toFixed(2)),
          status: "paid",
          stripeInvoiceId: inv.id as string,
        }).onConflictDoNothing();

        await db
          .update(subscriptionsTable)
          .set({ status: "active", updatedAt: new Date() })
          .where(eq(subscriptionsTable.stripeSubscriptionId, subscriptionId));

        // Notify company users about successful invoice payment — fire-and-forget
        const companyUser = await db
          .select({ email: usersTable.email, firstName: usersTable.firstName, language: usersTable.language })
          .from(usersTable)
          .where(eq(usersTable.companyId, sub[0].companyId))
          .limit(1);
        if (companyUser[0]) {
          const isAr = companyUser[0].language === "ar";
          const planName = sub[0].planId
            ? (await db.select({ name: plansTable.name }).from(plansTable).where(eq(plansTable.id, sub[0].planId)).limit(1))[0]?.name ?? ""
            : "";
          sendMail({
            to: companyUser[0].email,
            subject: isAr ? `تم الدفع بنجاح – ${planName}` : `Invoice Paid – ${planName}`,
            html: subscriptionEventHtml(companyUser[0].firstName ?? "", "invoice_paid", planName, isAr),
          }).catch(() => {});
        }
        break;
      }

      case "invoice.payment_failed": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inv = event.data.object as any;
        const subscriptionId = inv.subscription as string | null;
        if (!subscriptionId) break;
        await db
          .update(subscriptionsTable)
          .set({ status: "past_due", updatedAt: new Date() })
          .where(eq(subscriptionsTable.stripeSubscriptionId, subscriptionId));
        break;
      }
    }

    res.json({ received: true });
  } catch (err) {
    next(err);
  }
});

export default router;
